import './TalkBar.css';
import { MicControl } from './MicControl';
import { TalkBarPersonaPicker } from './TalkBarPersonaPicker';
import { useOptionalTtsQueue } from '../../contexts/TtsQueueContext';
import { useMicActivation } from '../../hooks/useMicActivation';

/**
 * V4.8 Voice GM (2026-06-02) — persistent talk mini-UI at the top of
 * the runner.
 *
 * User feedback: the talk controls (🎤 mic + activation mode) live in
 * the Audio drawer (right side), but the player has to scroll the
 * drawer to reach them. This bar sits BETWEEN the runner header and
 * the main content so the player can always see + use the mic + see
 * what the TTS queue is doing.
 *
 * Contains:
 *   - Status pill (idle / loading / playing / cancelled) showing what
 *     the global TTS queue is doing right now
 *   - Mic activation mode chip (TOGGLE / PTT) — drawer Audio section
 *     still has the radio picker; this is read-only
 *   - The actual MicControl mic button — clicking it cancels TTS +
 *     records (V4 magic) regardless of scroll position
 *
 * onTranscript is the same callback ScenarioRunner already passes to
 * the in-paragraph MicControls; routing it here too means a transcript
 * from the persistent mic goes through the same useGMDispatcher loop.
 */

export interface TalkBarProps {
  onTranscript?: (text: string) => void;
}

const QUEUE_STATE_LABEL: Record<string, string> = {
  idle: 'IDLE',
  loading: 'LOADING',
  playing: 'NARRATING',
  cancelled: 'CANCELLED',
};

export function TalkBar({ onTranscript }: TalkBarProps) {
  const ttsQueue = useOptionalTtsQueue();
  const [activation] = useMicActivation();

  const queueState = ttsQueue?.state ?? 'idle';
  const queueLabel = QUEUE_STATE_LABEL[queueState] ?? queueState.toUpperCase();
  const activationLabel = activation === 'ptt' ? 'PTT' : 'TOGGLE';

  return (
    <div
      className="br-talk-bar"
      data-testid="br-talk-bar"
      data-queue-state={queueState}
    >
      <span
        className="br-talk-bar-status br-label"
        data-testid="br-talk-bar-status"
        data-state={queueState}
      >
        <span className="br-talk-bar-status-dot" aria-hidden="true" />
        <span className="br-talk-bar-status-label">TTS: {queueLabel}</span>
      </span>
      <MicControl
        testidPrefix="br-mic-talkbar"
        onTranscript={onTranscript}
      />
      <TalkBarPersonaPicker />
      <span
        className="br-talk-bar-activation br-label"
        data-testid="br-talk-bar-activation"
      >
        {activationLabel}
      </span>
      <span className="br-talk-bar-hint br-label" aria-hidden="true">
        Press 🎤 to interrupt + reply
      </span>
    </div>
  );
}

export default TalkBar;
